import { skipToken } from '@reduxjs/toolkit/query'
import {
  toLiveUpdatesPublishError,
  useGetLiveUpdatesConfigurationQuery,
  usePublishLiveUpdatesConfigurationMutation,
} from 'api/liveUpdates'
import type {
  LiveUpdatesConfigurationDetail,
  LiveUpdatesContentFieldKey,
  LiveUpdatesPublishError,
  LiveUpdatesPublishStatus,
  LiveUpdatesStatus,
  LiveUpdatesStatusKey,
  LiveUpdatesTriggerMode,
} from 'api/liveUpdates.types'
import { useCallback, useEffect, useMemo, useState } from 'react'

// One status's in-progress edits. Only the fields the merchant actually touched
// are present — the publish payload derives from exactly these keys (minus any
// that turned out to equal the server value), so an omitted key stays untouched
// server-side. Content fields are held as plain strings ('' means "cleared",
// which the server nulls back to the default).
export type StatusEdits = {
  enabled?: boolean
  triggerMode?: LiveUpdatesTriggerMode
  timerOffset?: number
  statusTitle?: string
  pushMessageText?: string
  homescreenText?: string
}

type EditBuffer = Partial<Record<LiveUpdatesStatusKey, StatusEdits>>

// Maps the three content-field keys to their nested spot on a server status so
// overlay + dirty comparison can walk them generically.
const CONTENT_FIELD_KEYS: LiveUpdatesContentFieldKey[] = [
  'statusTitle',
  'pushMessageText',
  'homescreenText',
]

// Mirrors nucleus OrderStatus::StatusConfiguration::STATUS_DEFAULTS'
// default_timer_offset for the two timer-eligible statuses. Seeds the offset
// when a switch to 'timer' would otherwise leave it null (AI-863) — the
// server rejects a 'timer' status with no offset, so the invalid state must
// never reach the buffer. Only a fallback preference, not the sole source of
// truth: seedTimerOffset below validates it against the status's own
// server-supplied timerOffsetValues before using it, so a future drift
// between this map and nucleus's real default degrades to a valid offset
// instead of silently seeding one the dropdown can't even display.
const DEFAULT_TIMER_OFFSET: Partial<Record<LiveUpdatesStatusKey, number>> = {
  preparing: 10,
  ready: 0,
}

// Picks the offset to seed when switching a status to 'timer' with no offset
// set. Prefers the nucleus-mirrored default, but only when the status's own
// timerOffsetValues actually offers it — otherwise falls back to the first
// value the server says is valid for this status, so an unmapped or
// drifted status still seeds something selectable rather than nothing.
const seedTimerOffset = (
  status: LiveUpdatesStatusKey,
  timerOffsetValues: number[]
): number => {
  const preferred = DEFAULT_TIMER_OFFSET[status]
  if (preferred !== undefined && timerOffsetValues.includes(preferred)) {
    return preferred
  }
  return timerOffsetValues[0] ?? 0
}

export type UseLiveUpdatesConfiguration = {
  detail: LiveUpdatesConfigurationDetail | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
  statuses: LiveUpdatesStatus[]
  edits: EditBuffer
  setEnabled: (status: LiveUpdatesStatusKey, enabled: boolean) => void
  setTriggerMode: (
    status: LiveUpdatesStatusKey,
    mode: LiveUpdatesTriggerMode
  ) => void
  setTimerOffset: (status: LiveUpdatesStatusKey, minutes: number) => void
  setContent: (
    status: LiveUpdatesStatusKey,
    field: LiveUpdatesContentFieldKey,
    value: string
  ) => void
  isDirty: boolean
  dirtyStatuses: LiveUpdatesStatusKey[]
  // Resolves true when the publish succeeded (or was a no-op), false when the
  // request failed — callers use this to decide whether to complete a staged
  // action or surface the error banner. Never rejects.
  publish: () => Promise<boolean>
  isPublishing: boolean
  publishError: LiveUpdatesPublishError | null
  publishSucceeded: boolean
  discardEdits: () => void
}

// The server's current value for a content field, normalized to the string the
// edit buffer compares against ('' when the server has no value / the default).
const serverContentValue = (
  status: LiveUpdatesStatus,
  field: LiveUpdatesContentFieldKey
): string => status.content[field].value ?? ''

// Reduces one status's raw buffer to only the fields that actually differ from
// the server — the source of truth for dirty tracking AND the minimal publish
// payload. A buffered value equal to the server value is dropped (not dirty).
const changedFields = (
  status: LiveUpdatesStatus,
  statusEdits: StatusEdits
): StatusEdits => {
  const changed: StatusEdits = {}
  if (
    statusEdits.enabled !== undefined &&
    statusEdits.enabled !== status.enabled
  ) {
    changed.enabled = statusEdits.enabled
  }
  if (
    statusEdits.triggerMode !== undefined &&
    statusEdits.triggerMode !== status.triggerMode
  ) {
    changed.triggerMode = statusEdits.triggerMode
  }
  // A timer offset only matters in 'timer' mode. Ignore a buffered offset when
  // the status's effective (overlaid) mode is 'event' — e.g. the merchant picked
  // an offset then reverted to Event: the offset dropdown is hidden in Event
  // mode, so a leftover offset would otherwise keep the status dirty forever
  // (un-revertable) and publish a stray timer_offset on an event-triggered
  // status.
  const effectiveTriggerMode = statusEdits.triggerMode ?? status.triggerMode
  if (
    effectiveTriggerMode === 'timer' &&
    statusEdits.timerOffset !== undefined &&
    statusEdits.timerOffset !== status.timerOffset
  ) {
    changed.timerOffset = statusEdits.timerOffset
  }
  CONTENT_FIELD_KEYS.forEach(field => {
    const edited = statusEdits[field]
    if (edited === undefined) return
    // A field the server marked non-editable (e.g. push_message_text on a
    // non-cancel status, where the key is omitted entirely and fails soft to
    // editable: false) can never have been genuinely edited through the UI —
    // the input is disabled — so never publish it. Gated on the server's own
    // editable signal, not a hardcoded field/status list, so this can't drift
    // from the backend's push-eligibility rule.
    if (!status.content[field].editable) return
    // Whitespace-only input is the cleared-field sentinel ('') — the server nulls
    // a cleared field back to its default. Normalizing here (rather than in
    // setContent, so the controlled input never fights the cursor) keeps
    // whitespace-only from counting as a real edit and from publishing literal
    // spaces as customer-facing copy.
    const normalized = edited.trim() === '' ? '' : edited
    if (normalized !== serverContentValue(status, field)) {
      changed[field] = normalized
    }
  })
  return changed
}

// Edit-buffer + publish-state hook for the Live Updates (Order Status V1)
// merchant config page. Reads the configuration detail via RTK Query, keeps a
// per-status edit buffer overlaid on the server truth, tracks per-field dirtiness
// (a buffered value equal to the server value is NOT dirty), and publishes only
// the minimal set of changed statuses/fields with the server's current version
// as the optimistic-concurrency base. The edit buffer is transient draft state
// scoped to this one scene — held in component state the way the repo's Formsy
// forms hold in-progress edits, and co-located in a useXxx hook per the "extract
// to a co-located useXxx.ts when 3+ hooks accumulate" convention. The published
// truth lives in the RTK Query cache (the publish mutation writes it back), so
// this buffer never doubles as the source of truth for saved data.
export function useLiveUpdatesConfiguration(
  configurationId: string | null,
  enableLiveUpdates = false
): UseLiveUpdatesConfiguration {
  const {
    data: detail,
    isLoading,
    isError,
    refetch,
  } = useGetLiveUpdatesConfigurationQuery(configurationId ?? skipToken)

  const [
    publishConfiguration,
    { isLoading: isPublishing, reset: resetMutation },
  ] = usePublishLiveUpdatesConfigurationMutation()

  const [edits, setEdits] = useState<EditBuffer>({})
  const [publishError, setPublishError] =
    useState<LiveUpdatesPublishError | null>(null)
  const [publishSucceeded, setPublishSucceeded] = useState(false)

  // Switching configurations resets everything: the buffer belongs to the old
  // record, and the mutation's error/result state is instance-scoped and would
  // otherwise carry a stale error banner (or success flag) across records
  // (CLAUDE.md: reset() the mutation, key state to the route param).
  useEffect(() => {
    setEdits({})
    setPublishError(null)
    setPublishSucceeded(false)
    resetMutation()
  }, [configurationId, resetMutation])

  const serverStatuses = useMemo(() => detail?.statuses ?? [], [detail])

  // Patch one status's buffer and drop any stale success flag — any edit means
  // the last publish no longer describes the current buffer. `patch` can be a
  // flat object merged onto the prior buffer, or a function of the prior
  // buffer (returning the full new StatusEdits) for setters that need to
  // branch on what's already there — reading it via this functional updater,
  // not the outer `edits` closure, keeps them correct even when a caller
  // fires multiple setters back to back before a re-render lands.
  const patchStatus = useCallback(
    (
      status: LiveUpdatesStatusKey,
      patch: StatusEdits | ((prev: StatusEdits | undefined) => StatusEdits)
    ) => {
      setEdits(prev => ({
        ...prev,
        [status]:
          typeof patch === 'function'
            ? patch(prev[status])
            : { ...prev[status], ...patch },
      }))
      setPublishSucceeded(false)
    },
    []
  )

  const setEnabled = useCallback(
    (status: LiveUpdatesStatusKey, enabled: boolean) => {
      patchStatus(status, { enabled })
    },
    [patchStatus]
  )

  const setTriggerMode = useCallback(
    (status: LiveUpdatesStatusKey, mode: LiveUpdatesTriggerMode) => {
      const serverStatus = serverStatuses.find(s => s.status === status)
      patchStatus(status, prevStatusEdits => {
        const patch: StatusEdits = { ...prevStatusEdits, triggerMode: mode }
        if (mode === 'timer') {
          const currentOffset =
            prevStatusEdits?.timerOffset ?? serverStatus?.timerOffset
          if (currentOffset == null) {
            patch.timerOffset = seedTimerOffset(
              status,
              serverStatus?.timerOffsetValues ?? []
            )
          }
        }
        return patch
      })
    },
    [patchStatus, serverStatuses]
  )

  const setTimerOffset = useCallback(
    (status: LiveUpdatesStatusKey, minutes: number) => {
      patchStatus(status, { timerOffset: minutes })
    },
    [patchStatus]
  )

  const setContent = useCallback(
    (
      status: LiveUpdatesStatusKey,
      field: LiveUpdatesContentFieldKey,
      value: string
    ) => {
      patchStatus(status, { [field]: value })
    },
    [patchStatus]
  )

  const discardEdits = useCallback(() => {
    setEdits({})
    setPublishError(null)
    setPublishSucceeded(false)
    resetMutation()
  }, [resetMutation])

  // Server statuses with the buffer overlaid (raw buffered values, whether or
  // not they differ from the server) so the UI reflects in-progress edits.
  const statuses = useMemo<LiveUpdatesStatus[]>(
    () =>
      serverStatuses.map(status => {
        const statusEdits = edits[status.status]
        if (!statusEdits) return status
        return {
          ...status,
          enabled: statusEdits.enabled ?? status.enabled,
          triggerMode: statusEdits.triggerMode ?? status.triggerMode,
          timerOffset: statusEdits.timerOffset ?? status.timerOffset,
          content: {
            statusTitle: {
              ...status.content.statusTitle,
              value:
                statusEdits.statusTitle ?? status.content.statusTitle.value,
            },
            pushMessageText: {
              ...status.content.pushMessageText,
              value:
                statusEdits.pushMessageText ??
                status.content.pushMessageText.value,
            },
            homescreenText: {
              ...status.content.homescreenText,
              value:
                statusEdits.homescreenText ??
                status.content.homescreenText.value,
            },
          },
        }
      }),
    [serverStatuses, edits]
  )

  // Only statuses with at least one field that genuinely differs from the server.
  const dirtyStatuses = useMemo<LiveUpdatesStatusKey[]>(
    () =>
      serverStatuses
        .filter(status => {
          const statusEdits = edits[status.status]
          return (
            statusEdits !== undefined &&
            Object.keys(changedFields(status, statusEdits)).length > 0
          )
        })
        .map(status => status.status),
    [serverStatuses, edits]
  )

  const isDirty = dirtyStatuses.length > 0

  const publish = useCallback(async () => {
    // No-op when nothing genuinely changed (avoids a version-bumping PUT that
    // would only churn the server's current_version). Also a no-op when the
    // detail can't report a numeric version (currentVersion === null): the scene
    // surfaces the load-failed state instead, so this path is unreachable from
    // the UI, but guarding it keeps a fabricated base_version off the wire.
    if (
      !detail ||
      detail.currentVersion === null ||
      (dirtyStatuses.length === 0 && !enableLiveUpdates)
    ) {
      return true
    }

    const dirtyStatusSet = new Set(dirtyStatuses)
    const publishStatuses: LiveUpdatesPublishStatus[] = serverStatuses
      .filter(status => dirtyStatusSet.has(status.status))
      .map(status => ({
        status: status.status,
        ...changedFields(status, edits[status.status] ?? {}),
      }))

    try {
      await publishConfiguration({
        id: detail.id,
        baseVersion: detail.currentVersion,
        statuses: publishStatuses,
        enableLiveUpdates,
      }).unwrap()
      // The mutation writes its response straight into the detail cache
      // (onQueryStarted + upsertQueryData), so the cache already holds the new
      // truth (current_version + statuses) the instant we clear the buffer — no
      // stale-cache revert flash, and the next publish reads the fresh
      // base_version. The scene also freezes every editor (and the picker) while
      // `isPublishing`, so no in-flight edit can land in the buffer between the
      // request and this clear.
      setEdits({})
      setPublishError(null)
      setPublishSucceeded(true)
      return true
    } catch (error) {
      // Keep the buffer intact so the merchant can retry (or, on 409, reload via
      // refetch() + discardEdits()).
      setPublishError(toLiveUpdatesPublishError(error))
      setPublishSucceeded(false)
      return false
    }
  }, [
    detail,
    dirtyStatuses,
    serverStatuses,
    edits,
    publishConfiguration,
    enableLiveUpdates,
  ])

  return {
    detail,
    isLoading,
    isError,
    refetch,
    statuses,
    edits,
    setEnabled,
    setTriggerMode,
    setTimerOffset,
    setContent,
    isDirty,
    dirtyStatuses,
    publish,
    isPublishing,
    publishError,
    publishSucceeded,
    discardEdits,
  }
}
